/**
 * SourcesPage — central management for every external integration the
 * merchant binds to flows. Two cards today (Cost+ projects, Shopify);
 * stub cards for upcoming integrations (WooCommerce, PrestaShop, custom
 * API).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getApiClient } from "../lib/apiClient";
import type { Flow } from "../types/flow";
import { useSourcesStore } from "../features/sources/sourcesStore";
import { CostplusProjectsCard } from "../features/sources/CostplusProjectsCard";
import { ShopifyStoresCard } from "../features/sources/ShopifyStoresCard";
import { MerchantMenu } from "../components/MerchantMenu";
import { ThemeToggle } from "../components/ThemeToggle";

export function SourcesPage() {
  const [flows, setFlows] = useState<Flow[]>([]);
  const refresh = useSourcesStore((s) => s.refresh);

  useEffect(() => {
    let cancelled = false;
    void refresh();
    getApiClient()
      .flows.list()
      .then((list) => {
        if (!cancelled) setFlows(list);
      })
      .catch(() => {
        if (!cancelled) setFlows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-neutral-200 dark:border-neutral-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/flows"
            className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="w-4 h-4" />
            Flows
          </Link>
          <h1 className="text-xl font-semibold">Sources</h1>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <MerchantMenu />
        </div>
      </header>
      <main className="flex-1 px-6 py-6 max-w-4xl w-full mx-auto space-y-6">
        <CostplusProjectsCard flows={flows} />

        <ShopifyStoresCard />

        <ComingSoonCard
          title="API / Custom"
          description="Direct API integration with bearer token auth — for merchants who roll their own commerce backend."
        />
        <ComingSoonCard
          title="WooCommerce"
          description="WordPress plugin + OAuth handshake. Same flow model as Shopify."
        />
        <ComingSoonCard
          title="PrestaShop"
          description="Installable module + OAuth. Coming after WooCommerce."
        />
      </main>
    </div>
  );
}

function ComingSoonCard({ title, description }: { title: string; description: string }) {
  return (
    <section className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/30 px-4 py-5">
      <div className="flex items-center justify-between">
        <p className="font-medium text-neutral-700 dark:text-neutral-300 text-sm">{title}</p>
        <span className="text-[10px] uppercase tracking-wide text-neutral-400">
          Coming soon
        </span>
      </div>
      <p className="mt-1 text-xs text-neutral-500">{description}</p>
    </section>
  );
}
